import React, { useCallback, useReducer, useState } from 'react';
import {
  Box, Button, CircularProgress, Divider, FormControl, InputLabel,
  MenuItem, Paper, Select, TextField, Toolbar, Tooltip, Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from 'react-query';
import { PrintTemplateService } from '../../../service/printTemplateService';
import SectionPalette from './SectionPalette';
import SectionCanvas from './SectionCanvas';
import SectionConfig from './SectionConfig';
import TemplateHTMLPreview from './TemplateHTMLPreview';

const DOC_TYPES = [
  { value: 'invoice',           label: 'Invoice' },
  { value: 'insurance_invoice', label: 'Insurance Invoice' },
  { value: 'rx',                label: 'Prescription (Rx)' },
  { value: 'quote',             label: 'Quote' },
  { value: 'receipt',           label: 'Receipt' },
  { value: 'eye_exam_summary',  label: 'Eye Exam Summary' },
];

function buildInitial(template, locationState) {
  if (template) {
    return {
      name: template.name || '',
      description: template.description || '',
      documentType: template.documentType || 'invoice',
      isDefault: !!template.isDefault,
      sections: template.templateData?.sections || [],
      pageSettings: template.templateData?.pageSettings || {},
    };
  }
  return {
    name: '',
    description: '',
    documentType: locationState?.documentType || 'invoice',
    isDefault: false,
    sections: [],
    pageSettings: { format: 'A4' },
  };
}

function formReducer(state, patch) {
  return { ...state, ...patch };
}

export default function PrintTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id;

  const [form, dispatch] = useReducer(formReducer, buildInitial(null, location.state));
  const [selectedSection, setSelectedSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [livePreviewOpen, setLivePreviewOpen] = useState(false);

  // Load existing template
  const { isLoading: loadingTemplate } = useQuery(
    ['printTemplate', id],
    () => PrintTemplateService.getById(id),
    { enabled: !isNew, onSuccess: (res) => dispatch(buildInitial(res.data, null)) }
  );

  // Load token catalog for the current document type
  const { data: catalogRes } = useQuery(
    ['tokenCatalog'],
    () => PrintTemplateService.getTokenCatalog(),
    { staleTime: Infinity }
  );
  const tokenCatalog = catalogRes?.data?.[form.documentType] || [];

  // Section operations
  const addSection = useCallback((sectionType) => {
    dispatch({
      sections: [
        ...form.sections,
        { type: sectionType.type, title: sectionType.label }
      ]
    });
  }, [form.sections]);

  const moveSection = useCallback((fromIdx, toIdx) => {
    const next = [...form.sections];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    dispatch({ sections: next });
    if (selectedSection === fromIdx) setSelectedSection(toIdx);
  }, [form.sections, selectedSection]);

  const removeSection = useCallback((idx) => {
    const next = form.sections.filter((_, i) => i !== idx);
    dispatch({ sections: next });
    setSelectedSection(null);
  }, [form.sections]);

  const updateSection = useCallback((idx, updated) => {
    const next = form.sections.map((s, i) => i === idx ? updated : s);
    dispatch({ sections: next });
  }, [form.sections]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        documentType: form.documentType,
        isDefault: form.isDefault,
        templateData: {
          pageSettings: form.pageSettings,
          sections: form.sections,
        },
      };
      if (isNew) {
        const res = await PrintTemplateService.create(payload);
        toast.success('Template created');
        navigate(`/settings/print-templates/${res.data.id}/edit`, { replace: true });
      } else {
        await PrintTemplateService.update(id, payload);
        toast.success('Template saved');
      }
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (isNew) { toast.info('Save the template first to preview it'); return; }
    setPreviewLoading(true);
    try {
      const blob = await PrintTemplateService.preview(id);
      const url = URL.createObjectURL(blob.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!isNew && loadingTemplate) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Top toolbar */}
      <Toolbar variant="dense" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Tooltip title="Back to list">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings/print-templates')}>
            Templates
          </Button>
        </Tooltip>
        <Typography variant="h6" sx={{ flex: 1, ml: 1 }} noWrap>
          {isNew ? 'New Template' : (form.name || 'Edit Template')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={previewLoading ? <CircularProgress size={16} /> : <PreviewIcon />}
          onClick={handlePreview}
          disabled={previewLoading || isNew}
          title="Open PDF preview in new tab (requires save)"
        >
          PDF
        </Button>
        <Button
          variant={livePreviewOpen ? 'contained' : 'outlined'}
          color={livePreviewOpen ? 'info' : 'inherit'}
          startIcon={livePreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
          onClick={() => setLivePreviewOpen(v => !v)}
        >
          {livePreviewOpen ? 'Hide Preview' : 'Live Preview'}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save
        </Button>
      </Toolbar>

      {/* Meta fields */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <TextField
          label="Template Name"
          size="small"
          required
          value={form.name}
          onChange={(e) => dispatch({ name: e.target.value })}
          sx={{ minWidth: 240 }}
        />
        <TextField
          label="Description"
          size="small"
          value={form.description}
          onChange={(e) => dispatch({ description: e.target.value })}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!isNew}>
          <InputLabel>Document Type</InputLabel>
          <Select
            label="Document Type"
            value={form.documentType}
            onChange={(e) => dispatch({ documentType: e.target.value })}
          >
            {DOC_TYPES.map(dt => <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Page Size</InputLabel>
          <Select
            label="Page Size"
            value={form.pageSettings?.format || 'A4'}
            onChange={(e) => dispatch({ pageSettings: { ...form.pageSettings, format: e.target.value } })}
          >
            {['A4', 'Letter', 'Legal'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* 3-panel editor layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', transition: 'all .2s' }}>
        {/* Left: Section palette */}
        <Paper variant="outlined" sx={{ width: 240, overflow: 'auto', borderRadius: 0, flexShrink: 0 }}>
          <SectionPalette onAdd={addSection} />
        </Paper>

        <Divider orientation="vertical" />

        {/* Middle: Canvas */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
          <SectionCanvas
            sections={form.sections}
            selectedIndex={selectedSection}
            onSelect={setSelectedSection}
            onMove={moveSection}
            onRemove={removeSection}
          />
        </Box>

        <Divider orientation="vertical" />

        {/* Right: Section config */}
        <Paper variant="outlined" sx={{ width: 300, overflow: 'auto', borderRadius: 0, flexShrink: 0 }}>
          <SectionConfig
            section={selectedSection !== null ? form.sections[selectedSection] : null}
            tokenCatalog={tokenCatalog}
            onChange={(updated) => updateSection(selectedSection, updated)}
          />
        </Paper>

        {/* Live preview panel */}
        {livePreviewOpen && (
          <>
            <Divider orientation="vertical" />
            <Box sx={{ width: 660, overflow: 'auto', flexShrink: 0, bgcolor: '#e8e8e8' }}>
              <TemplateHTMLPreview
                sections={form.sections}
                documentType={form.documentType}
                pageSettings={form.pageSettings}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
